import FormInputComponent from "@via/components/inputs/form-input-component";
import SelectComponent from "@via/components/select/select";
import {
  createMetadataFieldOptions,
  requiredValidation,
} from "@via/utils/form-validators";
import { VmetaFieldType } from "common";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FieldFormData } from "@via/pages/data/premium-dataset/utils/helper";

interface AdditionalFieldFormProps {
  columnName: string;
  onFieldDataChange: (data: FieldFormData) => void;
  initialData?: FieldFormData;
  isSelected?: boolean;
  onSelectionChange?: (isSelected: boolean) => void;
}

const FIELD_TYPE_OPTIONS = [
  { label: "String", value: VmetaFieldType.String },
  { label: "Number", value: VmetaFieldType.Number },
  { label: "Boolean", value: VmetaFieldType.Boolean },
  { label: "Date", value: VmetaFieldType.Date },
] as const;

const AdditionalFieldForm: FC<AdditionalFieldFormProps> = ({
  columnName,
  onFieldDataChange,
  initialData,
  isSelected = false,
  onSelectionChange,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useForm<FieldFormData>({
    mode: "onChange",
    defaultValues: {
      name: initialData?.label || initialData?.name || columnName,
      label: initialData?.label || initialData?.name || columnName,
      type: initialData?.type || VmetaFieldType.String,
      unique: initialData?.unique || false,
      required: initialData?.required || false,
    },
  });

  const label = watch("label");
  const type = watch("type");
  const unique = watch("unique");
  const required = watch("required");

  useEffect(() => {
    onFieldDataChange({
      name: label, // backend normalizes spaces->"_" and uppercase->lowercase
      label,
      type,
      unique,
      required,
      originalColumnName: columnName, // Preserve original column name
    });
  }, [label, type, unique, required, columnName]);

  return (
    <div
      className={`space-y-4 rounded-lg border p-4 ${
        isSelected
          ? "border-indigo-300 bg-indigo-50"
          : "border-gray-200 bg-white"
      }`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">
          {" "}
          <span className="font-mono text-indigo-600">{columnName}</span>
        </h4>
        {onSelectionChange && (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={e => onSelectionChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            />
            <label className="ml-2 text-sm text-gray-700">
              Create this field
            </label>
          </div>
        )}
      </div>

      <div>
        <FormInputComponent
          control={control}
          name="label"
          label="Label"
          placeholder="Enter display label"
          showRequiredField
          validations={requiredValidation("Label")}
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Type
        </label>
        <SelectComponent
          control={control}
          name="type"
          rules={createMetadataFieldOptions.type}
          enableClear={false}
          options={
            FIELD_TYPE_OPTIONS.map(type => ({
              label: type.label,
              value: type.value,
            })) ?? []
          }
          placeholder="Select Type"
        />
        {errors.type && (
          <span className="text-xs text-rose-500">{errors.type.message}</span>
        )}
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2">
          <FormInputComponent
            control={control}
            name="unique"
            type="checkbox"
            className="h-6 w-6 -mt-2"
          />
          <span className="text-sm font-medium text-gray-900">Unique</span>
        </label>

        <label className="flex items-center gap-2">
          <FormInputComponent
            control={control}
            name="required"
            type="checkbox"
            className="h-6 w-6 -mt-2"
          />
          <span className="text-sm font-medium text-gray-900">Required</span>
        </label>
      </div>
    </div>
  );
};

export default AdditionalFieldForm;
